/**
 * Presentation ViewModels - Component-specific
 *
 * These types are only *value*-independent of the domain/application layer: no runtime
 * import of domain/application code is needed by components, since components only ever
 * see the Vm types below. The converters do use `import type` on the real domain models
 * so that any future divergence between a domain model and its Vm is caught by the
 * compiler instead of silently producing wrong/undefined fields at runtime. `import type`
 * is erased at compile time, so this adds no runtime coupling.
 */
import type { PhysicalStore } from '../../domain/models/PhysicalStore';
import type { StoreCityGroup } from '../../domain/models/StoreCityGroup';
import type {
  NearestPhysicalStoreInfo,
  StoreLocationWidgetData,
} from '../../domain/models/StoreLocationWidgetData';

export interface PhysicalStoreVm {
  id: number;
  sid: string;
  storeName: string;
  address: string;
  cityName: string;
  cityGroup: string;
  contactNumber: string;
  postCode: string;
  storeReviewLink: string | null;
  destinationPlaceId: string;
  storeTimings: string;
  whatsappNumber: string;
  storeUrl: string;
  latitude: number;
  longitude: number;
}

export interface StoreCityGroupVm {
  cityName: string;
  stores: PhysicalStoreVm[];
}

export interface NearestPhysicalStoreInfoVm {
  storeMessage: string;
  description: string;
  destinationPlaceId: string;
  latitude: string;
  longitude: string;
}

export interface StoreLocationWidgetVm {
  storeImageUrl: string;
  nearestStore: NearestPhysicalStoreInfoVm;
}

export function toPhysicalStoreVm(store: PhysicalStore): PhysicalStoreVm {
  return {
    id: store.id,
    sid: store.sid,
    storeName: store.storeName,
    address: store.address,
    cityName: store.cityName,
    cityGroup: store.cityGroup,
    contactNumber: store.contactNumber,
    postCode: store.postCode,
    storeReviewLink: store.storeReviewLink,
    destinationPlaceId: store.destinationPlaceId,
    storeTimings: store.storeTimings,
    whatsappNumber: store.whatsappNumber,
    storeUrl: store.storeUrl,
    latitude: store.latitude,
    longitude: store.longitude,
  };
}

export function toStoreCityGroupVm(group: StoreCityGroup): StoreCityGroupVm {
  return {
    cityName: group.cityName,
    stores: group.stores.map(toPhysicalStoreVm),
  };
}

export function toStoreCityGroupsVm(groups: StoreCityGroup[]): StoreCityGroupVm[] {
  return groups.map(toStoreCityGroupVm);
}

function toNearestPhysicalStoreInfoVm(info: NearestPhysicalStoreInfo): NearestPhysicalStoreInfoVm {
  return {
    storeMessage: info.storeMessage,
    description: info.description,
    destinationPlaceId: info.destinationPlaceId,
    latitude: info.latitude,
    longitude: info.longitude,
  };
}

export function toStoreLocationWidgetVm(data: StoreLocationWidgetData): StoreLocationWidgetVm {
  return {
    storeImageUrl: data.storeImageUrl,
    nearestStore: toNearestPhysicalStoreInfoVm(data.nearestStore),
  };
}
